import { gsap } from "gsap";
import type { ComponentInternalInstance } from "vue";
import { Options } from "./type";
/**
 * 1. 获取容器节点，给容器节点添加 overflow:hidden
 * 2. 将所有的子节点平移 100% 到容器外部。
 * # 核心
 * 3. gotoSlide(slideID,direction)//
 *    1. 如果不存在上一帧，则直接将slideID展示到容器上
 *    2. 如果存在上一帧，
 *        2.1 direction为正向，
 *        则将 现有的slideID从0移动到-100%，并且 移动当前 slideID从100%到0%
 *        2.2 direction为反向
 *        则将 现有的slideID从0移动到100%，并且 移动当前 slideID从-100%到0%
 * 4. 正向和方向可以通过 nextIndex currentIndex 比较得出
 */
export const useSlider = (options: Options) => {
    const { slideRef, slides, slidesDom, slidesNum, currentIndex, config } = createConfig(options);

    let isAutoPlay = config.isAutoPlay;
    let isAnimating = false;
    function gotoSlide(nextIndex: number, direction?: -1 | 1) {
        if (!direction) {
            if (nextIndex === 0) {
                //如果 0 的上一张
                if (currentIndex.value === 1) {
                    direction = -1;
                } else {
                    direction = 1;
                }
            } else {
                direction = nextIndex > (currentIndex!.value || 0) ? 1 : -1;
            }
        }

        if (isAnimating) return;

        const slidesValue = slidesDom.value;

        const { duration } = config;
        const nextSlide = slidesValue[nextIndex];
        if (currentIndex.value === null) {
            currentIndex.value = nextIndex;
            gsap.set(slidesValue[nextIndex], { x: "0%" });
            return;
        }

        const currentSlide = slidesValue[currentIndex.value];

        currentIndex.value = nextIndex;

        isAnimating = true;

        if (direction > 0) {
            gsap.to(currentSlide, { x: "-100%", duration });
            gsap.fromTo(nextSlide, { x: "100%" }, { x: "0%", duration });
        } else {
            gsap.to(currentSlide, { x: "100%", duration });
            gsap.fromTo(nextSlide, { x: "-100%" }, { x: "0%", duration });
        }
        gsap.delayedCall(duration, () => {
            isAnimating = false;
        });
    }

    function init() {
        if (!slideRef.value) return;

        // position: "absolute", width: "100%", height: "100%", top: "0", left: "0"
        gsap.set(slidesDom.value, { x: "100%" });

        // $navPrev.on("click", gotoPrevSlide);
        // $navNext.on("click", gotoNextSlide);
        // $startAutoplay.on("click", startAutoPlay);
        // $stopAutoplay.on("click", stopAutoPlay);
        gotoSlide(0, 1);
        if (isAutoPlay) startAutoPlay();
    }

    function gotoNextSlide() {
        let nextIndex = (currentIndex.value || 0) + 1;
        if (nextIndex >= slidesNum.value) {
            nextIndex = 0;
        }
        stopAutoPlay();
        gotoSlide(nextIndex, 1);
    }

    function gotoPrevSlide() {
        let nextIndex = (currentIndex.value || 0) - 1;
        if (nextIndex <= -1) {
            nextIndex = slidesNum.value - 1;
        }
        stopAutoPlay();
        gotoSlide(nextIndex, -1);
    }

    function play() {
        const { delay } = config;
        gotoNextSlide();
        gsap.delayedCall(delay, play);
    }

    function startAutoPlay(immediate?: boolean) {
        const { delay } = config;
        if (immediate != null) {
            immediate = false;
        }

        if (immediate) {
            gotoNextSlide();
        }
        gsap.delayedCall(delay, play);
    }

    function stopAutoPlay() {
        isAutoPlay = false;
        gsap.killTweensOf(play);
    }

    onMounted(() => {
        init();
    });

    onUnmounted(() => {
        stopAutoPlay();
        slideRef.value = undefined;
    });

    return [
        reactive({ slideRef, slides, slidesNum, currentIndex }),
        {
            gotoSlide,
            gotoPrevSlide,
            gotoNextSlide,
            stopAutoPlay,
            startAutoPlay,
            addSlide(instance: any) {
                slides.value.push(instance);
            },
            setCurrentIndex(value: number) {
                // currentIndex.value = value;
                gotoSlide(value);
            },
        },
    ] as const;
};

function createConfig(options: Options) {
    /**配置信息 */
    const config = {
        /**每次的动画时长 */
        duration: 1,
        /**每次一切换延迟多久 */
        delay: 10,
        /**是否自动播放 */
        isAutoPlay: true,
    };
    Object.assign(config, options);

    /**dom节点  */
    const slideRef = ref<HTMLElement>();
    /**slides集合 */
    const slides = ref<ComponentInternalInstance[]>([]);
    /**slidesDom集合 */
    const slidesDom = computed(() => slides.value.map((item) => item.vnode.el));
    /**slides的长度 */
    const slidesNum = computed(() => slides.value.length);
    /**当前选中的slideIndex */
    const currentIndex = ref<number | null>(null);

    return {
        slideRef,
        slides,
        slidesDom,
        slidesNum,
        currentIndex,
        config,
    };
}

// function createSlideAnimation(config: any, slidesDom: any, currentIndex: any) {
//     const { duration } = config;
//     let isAnimating = false;
//     return () => {
//         function gotoSlide(nextIndex: number, direction: -1 | 1) {
//             if (isAnimating) return;

//             const slidesValue = slidesDom.value;

//             const nextSlide = slidesValue[nextIndex];
//             if (currentIndex.value === null) {
//                 currentIndex.value = nextIndex;
//                 gsap.set(slidesValue[nextIndex], { x: "0%" });
//                 return;
//             }

//             const currentSlide = slidesValue[currentIndex.value];

//             // let direction = nextIndex - currentIndex;
//             // if (nextIndex === 0) {
//             //     //如果 0 的上一张
//             //     if (currentIndex === 1) {
//             //         direction = -1;
//             //     } else {
//             //         direction = 1;
//             //     }
//             // }

//             currentIndex.value = nextIndex;

//             isAnimating = true;

//             if (direction > 0) {
//                 gsap.to(currentSlide, { x: "-100%", duration });
//                 gsap.fromTo(nextSlide, { x: "100%" }, { x: "0%", duration });
//             } else {
//                 gsap.to(currentSlide, { x: "100%", duration });
//                 gsap.fromTo(nextSlide, { x: "-100%" }, { x: "0%", duration });
//             }
//             gsap.delayedCall(duration, () => {
//                 isAnimating = false;
//             });
//         }

//         function gotoNextSlide() {
//             let nextIndex = (currentIndex.value || 0) + 1;
//             if (nextIndex >= slidesNum.value) {
//                 nextIndex = 0;
//             }
//             stopAutoPlay();
//             gotoSlide(nextIndex, 1);
//         }

//         function gotoPrevSlide() {
//             let nextIndex = (currentIndex.value || 0) - 1;
//             if (nextIndex <= -1) {
//                 nextIndex = slidesNum.value - 1;
//             }
//             stopAutoPlay();
//             gotoSlide(nextIndex, -1);
//         }

//         function startAutoPlay(immediate?: boolean) {
//             const { delay } = config;
//             if (immediate != null) {
//                 immediate = false;
//             }

//             if (immediate) {
//                 gotoNextSlide();
//             }
//             gsap.delayedCall(delay, play);
//         }

//         function stopAutoPlay() {
//             isAutoPlay = false;
//             gsap.killTweensOf(play);
//         }

//         return { gotoSlide };
//     };
// }
